import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Breadcrumb, SimpleCard } from "app/components";
import Loading from "app/components/MatxLoading";
import { PAGE_LIMIT } from "../../../config";

import {
  getAllOvertimeRequests,
  approveOrRejectOvertime,
  deleteOvertimeRequest,
} from "app/redux/actions/overtimeActions";
import { GETALLUSERS } from "app/redux/actions/users";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import styled from "styled-components";
import EditIcon from "@mui/icons-material/Edit";
const HorizontalTable = styled.div`
  overflow-x: auto;
  @media (max-width: 600px) {
    width: 250%;
  }
`;

const AllOvertimeRequests = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ status: "", userId: "" });
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const loggedInUser = useSelector((state) => state.user.user);
  const handleEdit = (id) => {
    navigate(`/edit-overtime-request/${id}`);
  };
  const { allUsers } = useSelector((state) => state.user);

  useEffect(() => {
    if (loggedInUser.userType !== "employee") {
      dispatch(GETALLUSERS({ page: 1, limit: 1000 }, loggedInUser));
    }
  }, [dispatch, loggedInUser]);

  const fetchRequests = () => {
    setLoading(true);
    dispatch(getAllOvertimeRequests({ ...filters, page, limit: PAGE_LIMIT }))
      .then((res) => {
        setRequests(res.data);
        setTotalPages(res.totalPages);
      })
      .finally(() => setLoading(false));
  };

  useEffect(fetchRequests, [dispatch, filters, page]);

  const handleFilterChange = (event) => {
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  const handleAction = (id, isApproved) => {
    const statusData = { status: isApproved ? "Approved" : "Rejected" };

    dispatch(approveOrRejectOvertime(id, statusData))
      .then(() => {
        fetchRequests();
      })
      .catch((error) => {
        console.error("Action failed:", error);
      });
  };

  const handleView = (id) => {
    navigate(`/view-overtime-request/${id}`);
  };
  function decimalToTime(hoursDecimal) {
    if (isNaN(hoursDecimal) || hoursDecimal < 0) return "00:00";
    const totalMinutes = Math.floor(hoursDecimal * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}`;
  }
  if (loading && requests.length === 0) {
    return <Loading />;
  }
  const getStatusChip = (status) => {
    const color =
      status === "Approved"
        ? "success"
        : status === "Rejected"
        ? "error"
        : "warning";

    return (
      <Button
        variant="contained"
        color={color}
        size="small"
        sx={{ cursor: "default", boxShadow: "none" }}
      >
        {status}
      </Button>
    );
  };

  
  const handleOpenDeleteDialog = (id) => {
    setDeleteId(id);
    setOpenDialog(true);
  };

  const handleConfirmDelete = () => {
    if (deleteId) {
      dispatch(deleteOvertimeRequest(deleteId))
        .then(() => {
          fetchRequests(); 
        })
        .catch((error) => {
          console.error("Delete failed:", error);
        })
        .finally(() => {
          setOpenDialog(false); 
          setDeleteId(null);
        });
    }
  };
  return (
    <SimpleCard>
      <Breadcrumb routeSegments={[{ name: "All Overtime Requests" }]} />
      {loggedInUser.userType === "admin" && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/create-overtime-request")}
          >
            Create Overtime Request
          </Button>
        </Box>
      )}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {(loggedInUser.userType === "admin" ||
          loggedInUser.userType === "teamlead") && (
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Filter by Employee</InputLabel>
              <Select
                name="userId"
                value={filters.userId}
                onChange={handleFilterChange}
                label="Filter by Employee"
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {(allUsers?.users || []).map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Filter by Status</InputLabel>
            <Select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              label="Filter by Status"
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Approved">Approved</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ flexGrow: 1, overflow: "auto", paddingBottom: "70px" }}>
        <HorizontalTable>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ pl: 4 }}>Employee Name</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Hours Requested</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.length > 0 ? (
                  requests.map((req) => {
                    const isManager =
                      loggedInUser.userType === "admin" ||
                      loggedInUser.userType === "teamlead";
                    const isOwnRequest = req.User?.id === loggedInUser.id;

                    return (
                      <TableRow key={req.id}>
                        <TableCell sx={{ pl: 4 }}>
                          {req.User?.name || "N/A"}
                        </TableCell>
                        <TableCell>
                          {dayjs(req.date).format("DD/MM/YYYY")}
                        </TableCell>
                        <TableCell>{decimalToTime(req.hours)}</TableCell>
                        <TableCell>{getStatusChip(req.status)}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton onClick={() => handleView(req.id)}>
                              <VisibilityIcon color="primary" />
                            </IconButton>
                          </Tooltip>
                          {loggedInUser.userType === "admin" && (
                            <>
                              <Tooltip title="Edit">
                                <IconButton onClick={() => handleEdit(req.id)}>
                                  <EditIcon color="primary" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  onClick={() => handleOpenDeleteDialog(req.id)}
                                >
                                  <DeleteIcon color="error" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {req.status === "Pending" &&
                            (loggedInUser.userType === "admin" ||
                              (isManager && !isOwnRequest)) && (
                              <>
                                <Tooltip title="Approve">
                                  <IconButton
                                    onClick={() => handleAction(req.id, true)}
                                  >
                                    <DoneIcon color="success" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Reject">
                                  <IconButton
                                    onClick={() => handleAction(req.id, false)}
                                  >
                                    <CloseIcon color="error" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography>No overtime requests found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </HorizontalTable>
      </Box>

      {requests.length > 0 && totalPages > 1 && (
        <Box
          display="flex"
          justifyContent="center"
          sx={{
            py: 2,
            backgroundColor: "background.paper",
            boxShadow: "0 -2px 5px rgba(0,0,0,0.1)",
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, val) => setPage(val)}
            color="primary"
          />
        </Box>
      )}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this overtime request?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </SimpleCard>
  );
};

export default AllOvertimeRequests;
